import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { publishApi } from "../api/client";
import type { HereNowConnection, PublishTarget, SitePublication } from "../types";
import PublicationCard from "./PublicationCard";
import "./PublishDialog.css";

const ANONYMOUS = "anonymous";
const NEW_SITE = "new";

/**
 * Publish one canvas revision to here.now: update a site this canvas already
 * published, or create a new one — anonymously (24h) or into the user's Home
 * or a workspace, optionally on a custom domain the account already has.
 * Deliberately no access/name/folder settings: those live in here.now's
 * editor, which the result card links to.
 */
export default function PublishDialog({
  workspaceId,
  revisionId,
  onClose,
}: {
  workspaceId: string;
  revisionId: string;
  onClose(): void;
}) {
  const [connection, setConnection] = useState<HereNowConnection | null>(null);
  const [targets, setTargets] = useState<PublishTarget[]>([]);
  const [existing, setExisting] = useState<SitePublication[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetsError, setTargetsError] = useState<string | null>(null);
  const [site, setSite] = useState(NEW_SITE);
  const [destination, setDestination] = useState(ANONYMOUS);
  const [domain, setDomain] = useState("");
  const [mountPath, setMountPath] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SitePublication | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [conn, pubs] = await Promise.all([publishApi.connection(), publishApi.list({ workspace_id: workspaceId })]);
        if (cancelled) return;
        setConnection(conn);
        const live = pubs.filter((p) => p.status === "live");
        setExisting(live);
        // Re-publishing a canvas should update the site it already has.
        if (live.length) setSite(live[0].id);
        if (conn.connected) {
          try {
            const t = await publishApi.targets();
            if (cancelled) return;
            setTargets(t);
            if (t.length) setDestination(t[0].account_id);
          } catch (e) {
            if (!cancelled) setTargetsError((e as Error).message);
          }
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !publishing) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, publishing]);

  const target = useMemo(() => targets.find((t) => t.account_id === destination) ?? null, [targets, destination]);
  const updating = existing.find((p) => p.id === site) ?? null;

  const publish = async () => {
    setPublishing(true);
    setError(null);
    try {
      const body = updating
        ? { anonymous: updating.anonymous, publication_id: updating.id }
        : destination === ANONYMOUS
          ? { anonymous: true }
          : { anonymous: false, account_id: destination, domain: domain || null, mount_path: domain ? mountPath : null };
      setResult(await publishApi.publish(workspaceId, revisionId, body));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPublishing(false);
    }
  };

  // Portalled: the canvas pane may sit under a transformed ancestor, which
  // would otherwise turn this fixed overlay into a pane-relative box.
  return createPortal(
    <div className="publish-dialog-backdrop" onClick={() => !publishing && onClose()}>
      <div
        className="publish-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="publish-dialog-head">
          <h3 id="publish-dialog-title">Publish to here.now</h3>
          <button type="button" className="btn btn-sm btn-ghost" aria-label="Close" onClick={onClose} disabled={publishing}>✕</button>
        </div>

        {loading ? (
          <p className="publish-dialog-muted"><span className="spinner" /> Loading…</p>
        ) : result ? (
          <>
            <PublicationCard publication={result} connected={Boolean(connection?.connected)} />
            <div className="publish-dialog-foot">
              <span className="spacer" />
              <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            {existing.length > 0 && (
              <label className="publish-dialog-field">
                <span>Site</span>
                <select className="select" value={site} onChange={(e) => setSite(e.target.value)}>
                  {existing.map((p) => (
                    <option key={p.id} value={p.id}>
                      Update {p.primary_url.replace(/^https:\/\//, "").replace(/\/$/, "")}
                    </option>
                  ))}
                  <option value={NEW_SITE}>Publish as a new site</option>
                </select>
              </label>
            )}

            {!updating && (
              <>
                <label className="publish-dialog-field">
                  <span>Publish to</span>
                  <select className="select" value={destination} onChange={(e) => { setDestination(e.target.value); setDomain(""); }}>
                    {targets.map((t) => (
                      <option key={t.account_id} value={t.account_id} disabled={!t.ready}>
                        {t.type === "personal" ? "My here.now Home" : `${t.name} workspace`}{t.ready ? "" : " (setting up)"}
                      </option>
                    ))}
                    <option value={ANONYMOUS}>Anonymous — temporary link (24 hours)</option>
                  </select>
                </label>

                {target && target.domains.length > 0 && (
                  <div className="publish-dialog-row">
                    <label className="publish-dialog-field">
                      <span>Also serve on</span>
                      <select className="select" value={domain} onChange={(e) => setDomain(e.target.value)}>
                        <option value="">No custom domain</option>
                        {target.domains.map((d) => (
                          <option key={d.domain} value={d.domain}>
                            {d.domain}{d.status && d.status !== "active" ? ` (${d.status})` : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    {domain && (
                      <label className="publish-dialog-field">
                        <span>Path</span>
                        <input
                          className="input"
                          placeholder="(root)"
                          value={mountPath}
                          onChange={(e) => setMountPath(e.target.value)}
                        />
                      </label>
                    )}
                  </div>
                )}

                {destination === ANONYMOUS && (
                  <p className="publish-dialog-note">
                    Anyone with the link can view it for 24 hours. You'll get a one-time claim link to keep it.
                  </p>
                )}
                {target?.type === "org" && (
                  <p className="publish-dialog-note">
                    Workspace sites are members-only by default. Change access in here.now after publishing.
                  </p>
                )}
                {!connection?.connected && (
                  <p className="publish-dialog-muted">
                    Want permanent sites in your own space? <Link to="/plugins" onClick={onClose}>Connect here.now</Link>.
                  </p>
                )}
                {targetsError && <p className="publish-dialog-error">Could not load your here.now spaces: {targetsError}</p>}
              </>
            )}

            {updating && (
              <p className="publish-dialog-note">
                Replaces the current contents of this site with this revision. The URL stays the same.
              </p>
            )}

            {error && <p className="publish-dialog-error" role="alert">{error}</p>}

            <div className="publish-dialog-foot">
              <span className="spacer" />
              <button type="button" className="btn" onClick={onClose} disabled={publishing}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => void publish()} disabled={publishing}>
                {publishing ? <><span className="spinner" /> Publishing…</> : updating ? "Update site" : "Publish"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
